export type LaneType = 'core' | 'advanced' | 'specialty';

export type LaneOption = {
  readonly id: string;
  readonly label: string;
  readonly type: LaneType;
  readonly description: string;
  readonly sortOrder: number;
};

export type LaneRule =
  | {
      readonly mode: 'choose';
      readonly maxSelections: number;
      readonly allowedLaneIds: readonly string[];
      readonly advancedAccessLabel: string;
      readonly specialtyAccessLabel: string;
      readonly maxAdvancedSelections: number | 'all';
      readonly maxSpecialtySelections: number | 'all';
      readonly laneTypeById: Readonly<Record<string, LaneType>>;
    }
  | {
      readonly mode: 'all';
      readonly allowedLaneIds: readonly string[];
      readonly advancedAccessLabel: string;
      readonly specialtyAccessLabel: string;
      readonly maxAdvancedSelections: number | 'all';
      readonly maxSpecialtySelections: number | 'all';
      readonly laneTypeById: Readonly<Record<string, LaneType>>;
    };

type MetadataCarrier = {
  metadata?: Record<string, unknown> | null;
};

function safeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function safeNumber(value: unknown, fallback = 0): number {
  const parsed =
    typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function getTierMeta(
  tier: MetadataCarrier | null | undefined,
): Record<string, unknown> {
  return (tier?.metadata as Record<string, unknown> | undefined) ?? {};
}

export function getTierMetaValue(
  tier: MetadataCarrier | null | undefined,
  key: string,
): string {
  const value = getTierMeta(tier)[key];
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number') return String(value);
  return 'Not specified';
}

export function normalizeLaneType(value: unknown): LaneType {
  if (value === 'advanced' || value === 'specialty') return value;
  return 'core';
}

export function readServiceLaneOptions(
  service: MetadataCarrier | null | undefined,
): LaneOption[] {
  const rawCatalog =
    (service?.metadata as Record<string, unknown> | undefined)?.offeringsCatalog;
  if (!Array.isArray(rawCatalog)) return [];

  const laneOptions: LaneOption[] = [];
  for (const entry of rawCatalog) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const record = entry as Record<string, unknown>;
    const id = safeString(record.code);
    const label = safeString(record.name);
    if (!id || !label) continue;
    const status = safeString(record.status).toLowerCase();
    if (status === 'archived') continue;
    laneOptions.push({
      id,
      label,
      type: normalizeLaneType(record.type),
      description: safeString(record.description),
      sortOrder: safeNumber(record.sortOrder, laneOptions.length),
    });
  }

  return laneOptions.sort(
    (a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label),
  );
}

export function readTierAllowedLaneIds(
  tier: MetadataCarrier | null | undefined,
  laneOptions: readonly LaneOption[],
): readonly string[] {
  const metadata = getTierMeta(tier);
  const validCodes = new Set(laneOptions.map((option) => option.id));
  const metadataIncluded = Array.isArray(metadata.includedServiceOfferingCodes)
    ? metadata.includedServiceOfferingCodes
        .map((code) => safeString(code))
        .filter((code) => code && validCodes.has(code))
    : [];

  const allowedSet = new Set<string>(
    metadataIncluded.length ? metadataIncluded : laneOptions.map((option) => option.id),
  );

  return laneOptions
    .map((option) => option.id)
    .filter((code) => allowedSet.has(code));
}

export function parseActiveLaneSlots(
  tier: MetadataCarrier | null | undefined,
): number | 'all' {
  const activeLanes = getTierMeta(tier).activeLanesIncluded;
  if (typeof activeLanes === 'number' && Number.isInteger(activeLanes) && activeLanes > 0) {
    return activeLanes;
  }
  if (
    typeof activeLanes === 'string' &&
    activeLanes.toLowerCase() === 'all'
  ) {
    return 'all';
  }
  return 1;
}

export function parseLaneTypeAccessLimit(value: unknown): number | 'all' {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }

  const raw = safeString(value).toLowerCase();
  if (!raw || raw === 'not specified' || raw === '--') return 0;
  if (raw === 'all' || raw === 'yes' || raw === 'true' || raw === 'unlimited') return 'all';
  if (raw === 'none' || raw === 'no' || raw === 'false') return 0;

  const numericMatch = raw.match(/\d+/);
  if (numericMatch) {
    const parsed = Number.parseInt(numericMatch[0], 10);
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }

  if (raw.includes('limited')) return 1;
  return 0;
}

export function getLaneTypeLimit(
  rule: LaneRule,
  laneType: LaneType,
): number | 'all' {
  if (laneType === 'advanced') return rule.maxAdvancedSelections;
  if (laneType === 'specialty') return rule.maxSpecialtySelections;
  return 'all';
}

export function canSelectLane(
  rule: LaneRule,
  currentSelection: readonly string[],
  laneId: string,
): boolean {
  if (!rule.allowedLaneIds.includes(laneId)) return false;
  if (currentSelection.includes(laneId)) return true;
  if (rule.mode === 'all') return false;
  if (currentSelection.length >= rule.maxSelections) return false;

  const laneType = rule.laneTypeById[laneId] ?? 'core';
  const typeLimit = getLaneTypeLimit(rule, laneType);
  if (typeLimit === 'all') return true;

  const sameTypeCount = currentSelection.filter(
    (id) => (rule.laneTypeById[id] ?? 'core') === laneType,
  ).length;

  return sameTypeCount < typeLimit;
}

export function getLaneRuleForTier(
  tier: MetadataCarrier | null | undefined,
  laneOptions: readonly LaneOption[],
): LaneRule {
  const advancedAccessLabel = getTierMetaValue(tier, 'advancedLanesAccess');
  const specialtyAccessLabel = getTierMetaValue(tier, 'specialtyLanesAccess');
  const slots = parseActiveLaneSlots(tier);
  const tierAllowedLaneIds = readTierAllowedLaneIds(tier, laneOptions);
  const laneTypeById = Object.fromEntries(
    laneOptions.map((lane) => [lane.id, lane.type] as const),
  );

  const maxAdvancedSelections = parseLaneTypeAccessLimit(advancedAccessLabel);
  const maxSpecialtySelections = parseLaneTypeAccessLimit(specialtyAccessLabel);

  const allowedLaneIds = laneOptions
    .filter((lane) => tierAllowedLaneIds.includes(lane.id))
    .filter((lane) => {
      if (lane.type === 'core') return true;
      if (lane.type === 'advanced') {
        return maxAdvancedSelections === 'all' || maxAdvancedSelections > 0;
      }
      if (lane.type === 'specialty') {
        return maxSpecialtySelections === 'all' || maxSpecialtySelections > 0;
      }
      return true;
    })
    .map((lane) => lane.id);

  if (slots === 'all') {
    return {
      mode: 'all',
      allowedLaneIds,
      advancedAccessLabel,
      specialtyAccessLabel,
      maxAdvancedSelections,
      maxSpecialtySelections,
      laneTypeById,
    };
  }

  return {
    mode: 'choose',
    maxSelections: Math.max(0, Math.min(slots, allowedLaneIds.length)),
    allowedLaneIds,
    advancedAccessLabel,
    specialtyAccessLabel,
    maxAdvancedSelections,
    maxSpecialtySelections,
    laneTypeById,
  };
}

export function sanitizeLaneSelection(
  rule: LaneRule,
  selectedLaneIds: readonly string[] | undefined,
): string[] {
  const selected = Array.isArray(selectedLaneIds) ? selectedLaneIds : [];
  const uniqueAllowed: string[] = [];

  for (const laneId of selected) {
    if (rule.allowedLaneIds.includes(laneId) && !uniqueAllowed.includes(laneId)) {
      uniqueAllowed.push(laneId);
    }
  }

  if (rule.mode === 'all') {
    return [...rule.allowedLaneIds];
  }

  const filteredByType: string[] = [];
  const typeCounts: Record<LaneType, number> = {
    core: 0,
    advanced: 0,
    specialty: 0,
  };

  for (const laneId of uniqueAllowed) {
    const laneType = rule.laneTypeById[laneId] ?? 'core';
    const limit = getLaneTypeLimit(rule, laneType);
    if (limit !== 'all' && typeCounts[laneType] >= limit) continue;
    filteredByType.push(laneId);
    typeCounts[laneType] += 1;
    if (filteredByType.length >= rule.maxSelections) {
      return filteredByType.slice(0, rule.maxSelections);
    }
  }

  return filteredByType;
}
